import PropTypes from 'prop-types';
import React from 'react';
import Helmet from 'react-helmet';
import isElectron from 'is-electron';
import {
  components,
  contentfulViews,
  contentfulModels,
  contentfulUtils,
} from '@glowforge/ui-common';
import GlobalFooter from 'app/common/components/global-footer';
import Navigation from 'app/common/components/navigation/navigation';
import ErrorBoundary from 'app/common/components/error-boundary';

import '@glowforge/ui-common/stylesheets/base.less';
import './app.less';

const { interpretedMetadataPropType } = contentfulModels;
const { Notification } = components;
const { PageMetadata } = contentfulViews;
const { MetaTags } = contentfulUtils;

const meta = (canonicalUri) => {
  const tags = new MetaTags();
  tags.push({ 'char-set': 'utf-8' });
  tags.push({ 'http-equiv': 'x-ua-compatible', content: 'ie=edge' });

  tags.name('viewport', 'width=device-width');
  tags.name('robots', 'noodp');

  tags.openGraph('locale', 'en_US');
  // og:url must be a specific page's url blank to default to the current url.
  tags.openGraph('url', canonicalUri);

  return tags.tags;
};

const BasicApp = (props) => {
  const {
    noticeLevelError,
    clearGlobalError,
    showFooter,
    canonicalUri,
    className,
    pageTitle,
    defaultMetadata,
    children,
  } = props;

  let notification;
  if (noticeLevelError) {
    notification = (
      <div className='globalNotification'>
        <Notification
          title='Error'
          text={noticeLevelError}
          onDismiss={clearGlobalError}
          level='error' />
      </div>
    );
  }

  const footer = showFooter ? <GlobalFooter {...props} /> : null;

  const links = [{
    rel: 'alternate',
    type: 'application/rss+xml',
    title: 'Glowforge - the 3D Laser Printer » Home Comments Feed',
    href: 'https://glowforge.com/home/feed/',
  }];

  if (canonicalUri) {
    links.push({ rel: 'canonical', href: canonicalUri });
  }

  const navProps = { ...props };
  delete navProps.className;

  const { title: defaultTitle, ...trimmedMetadata } = defaultMetadata;

  // data for global cookie notification.
  const { cookieNotification } = props;

  // PageMetadata takes precedence over Helmet due to deeper component nesting.
  return (
    <div className={`App ${className}`}>
      <Helmet
        titleTemplate='%s | Glowforge'
        meta={meta(canonicalUri)}
        link={links} />
      <PageMetadata
        {...trimmedMetadata}
        pageTitle={pageTitle || defaultTitle || 'Glowforge'} />
      {!isElectron() && <Navigation {...navProps} />}
      {notification}
      <ErrorBoundary>
        {children}
      </ErrorBoundary>
      {footer}
    </div>
  );
};

BasicApp.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
  ]).isRequired,
  cookieNotification: PropTypes.shape({}),
  pageTitle: PropTypes.string,
  className: PropTypes.string,
  showFooter: PropTypes.bool,
  noticeLevelError: PropTypes.string,
  clearGlobalError: PropTypes.func.isRequired,
  includeBuyButton: PropTypes.bool,
  defaultMetadata: PropTypes.shape(interpretedMetadataPropType),
  canonicalUri: PropTypes.string,
  route: PropTypes.shape({ path: PropTypes.string }),
};

BasicApp.defaultProps = {
  cookieNotification: {},
  preloadError: null,
  noticeLevelError: null,
  className: '',
  pageTitle: null,
  showFooter: false,
  includeBuyButton: false,
  defaultMetadata: {},
  canonicalUri: null,
  route: { },
};

export default BasicApp;



// WEBPACK FOOTER //
// ./app/components/root-apps/basic-app.jsx